"use client";

import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { usePathname } from "next/navigation";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useConnectAccountPoll } from "../hooks/useConnectAccountPoll";
import { ModalContext } from "../hooks/useModal";
import {
  getSocialAccountHandle,
  hasAnyAuthenticatedSocialProviderAccount,
  useSocialProviderAccounts,
} from "../hooks/useSocialProviderAccounts";
import { providerOptions } from "./AddSocialProviderAccountButton";
import Button, { BUTTON_STYLE } from "./Button";
import { COMING_SOON_PROVIDERS } from "./NotConnectedSocialProviderAccountAvatar";
import SocialProviderEmptyState from "./SocialProviderEmptyState";
import { useUser } from "./SupabaseProvider";
import { useProfile } from "../hooks/useProfile";
import { needsPostBridgeRecoveryUpgrade } from "../helpers/accountRecoveryUpgrade";
import Typography from "./Typography";

type ConnectAccountsDialogProps = {
  onBack?: () => void;
  onConnected?: () => void;
  title?: string;
  subtitle?: string;
  onUpgrade?: () => void;
  /** Platforms the flow can't use (Auto publish never posts to LinkedIn). */
  hideProviders?: readonly string[];
};

const ConnectAccountsDialog: FC<ConnectAccountsDialogProps> = ({
  onBack,
  onConnected,
  title = "Connect an account",
  subtitle = "Choose a platform to continue.",
  onUpgrade,
  hideProviders,
}) => {
  const {
    readAccounts: { data: accounts, isLoading: accountsIsLoading, refetch },
  } = useSocialProviderAccounts();
  const { connect } = useConnectAccountPoll();
  const userId = useUser()?.id;
  const { data: profile } = useProfile();
  const needsUpgrade = needsPostBridgeRecoveryUpgrade(profile?.id === userId ? profile : undefined, accounts);
  const activationExpired = accounts?.some((account) => account.needsReconnect && account.activationExpired);
  // Ignore id-only placeholder accounts. If we auto-advanced on placeholders,
  // BulkCreatePublicationForm would filter them out and bounce straight back
  // here, yielding an infinite loop.
  const hasAccounts = hasAnyAuthenticatedSocialProviderAccount(accounts);
  const hasTriggeredConnectedRef = useRef(false);
  const modal = useContext(ModalContext);
  const pathname = usePathname();
  const [openedOnPathname] = useState(pathname);

  // The modal lives above the routes, so leaving the page this dialog gates
  // (its chevron, Escape, the overlay, the browser's back) would carry it
  // onto the next page: keep it up until the navigation commits, then close.
  useEffect(() => {
    if (pathname !== openedOnPathname && modal.isOpen) modal.close();
  }, [modal, openedOnPathname, pathname]);

  const providerOptionsCopy = useMemo(() => {
    const options = providerOptions.filter((option) => !hideProviders?.includes(option.value));
    options.sort((a, _b) => (COMING_SOON_PROVIDERS.includes(a.value) ? 1 : -1));
    return options;
  }, [hideProviders]);

  // The list is seeded from localStorage and counts as fresh for the
  // query's staleTime, so it can predate a connect made elsewhere: ask the
  // server once on open. Once the session is in — on a hard load it lands
  // after mount, and a refetch without it only re-reads that cache.
  useEffect(() => {
    if (userId) refetch();
  }, [userId, refetch]);

  // Coming back from the OAuth page: TanStack's own focus refetch only
  // listens to `visibilitychange`, which a tab switch fires but a switch back
  // from a separate window doesn't. A connect started here polls through
  // useConnectAccountPoll.
  useEffect(() => {
    if (hasAccounts) return;
    const handleFocus = () => { refetch(); };
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [hasAccounts, refetch]);

  useEffect(() => {
    if (!onConnected) return;
    if (accountsIsLoading) return;
    if (!hasAccounts) return;
    if (hasTriggeredConnectedRef.current) return;

    hasTriggeredConnectedRef.current = true;
    onConnected();
  }, [accountsIsLoading, hasAccounts, onConnected]);

  return (
    <div
      // The back chevron is the way out, so the enclosing Dialog's close X
      // stands down rather than doubling it (see `CONTENT_OWNS_CLOSE` in
      // components/Dialog.tsx). Without a chevron the X is the only exit.
      data-owns-dialog-close={onBack ? "" : undefined}
      className="flex flex-col"
    >
      <div className="relative flex min-h-9 items-center justify-center">
        {onBack ? (
          <Button variant="tertiary" size="m" onClick={onBack}>
            <ChevronLeftIcon />
          </Button>
        ) : null}
        <Typography variant="subtitle1" className="text-center w-full">
          {title}
        </Typography>
        <div className={twMerge(BUTTON_STYLE.m.width)} />
      </div>
      <Typography variant="body2" className="text-center w-full text-slate-500 mt-3">
        {subtitle}
      </Typography>
      <div className="flex flex-col gap-2 mt-3">
        {onUpgrade && needsUpgrade ? (
          <div className="flex flex-col gap-2 text-sm text-slate-600">
            <p>{activationExpired
              ? "Your YouTube connection expired. Upgrade, then reconnect to continue."
              : "Upgrade, then reconnect to restore your YouTube connection."}</p>
            <Button variant="primary" onClick={onUpgrade}>Choose a plan</Button>
          </div>
        ) : null}
        {accounts?.filter((account) => account.needsReconnect && !hideProviders?.includes(account.provider)).map((account) => (
          <Button
            key={`${account.provider}:${account.id}`}
            variant="secondary"
            onClick={() => connect(account.provider, account.id)}
            className="w-full"
          >
            Reconnect {getSocialAccountHandle(account) || providerOptionsCopy.find((option) => option.value === account.provider)?.label}
          </Button>
        ))}
        {providerOptionsCopy.map((provider) => (
          <SocialProviderEmptyState
            key={provider.value}
            provider={provider.value}
            labelProviderOnly
            onConnect={connect}
          />
        ))}
      </div>
    </div>
  );
};

export default ConnectAccountsDialog;
